import React from 'react';
import styled from '@emotion/styled';
import { Box, Container, Flex, Text } from '@qga/roo-ui/components';

import { SCREEN_SIZE } from '@shared/v2/constants';
import useBreakpoints, { isScreen } from '@shared/v2/hooks/useBreakpoints';

import Button from '@/components/Button';
import { useCustomerSelection } from '@/components/CustomerSelectionProvider';
import PackageHeroSummary from '@/components/PackageHeroSummary';
import useSearchQuery from '@/hooks/useSearchQuery';
import useSize from '@/shared/hooks/useSize';
import {
  PackagePagePriceWithCompactPointsPlusPay,
  PackagePagePriceWithPointsPlusPay,
} from '@/v2/components/pages/PackageView/components/PackagePagePrice';
import { useGA4Events } from '@/v2/hooks/featureFlags/ga4Events/useGA4Events';
import { Charges } from '@/v2/services/availability/types/api/parsedResponse';
import dataLayer from '@/v2/utils/dataLayer';
import {
  FLIGHT_DIRECTION,
  FlightDirection,
} from '../../constants/flightDirection';

const StyledButton = styled(Button)`
  ${SCREEN_SIZE.SM} {
    display: flex;
    height: 48px;
    padding: 12px 32px;
    justify-content: center;
    align-items: center;
  }
`;

const getConfirmButtonLabel = (direction: FlightSummaryProps['direction']) => {
  switch (direction) {
    case FLIGHT_DIRECTION.Departing:
      return 'Confirm departing flight';
    case FLIGHT_DIRECTION.Returning:
      return 'Confirm returning flight';
    default:
      return 'Confirm flights';
  }
};

interface FlightSummaryProps {
  onDone: () => void;
  charges: Charges;
  direction?: FlightDirection;
  offerId: string | undefined;
}

const FlightSummary = React.memo(
  ({ onDone, charges, direction, offerId }: FlightSummaryProps) => {
    const { searchQuery } = useSearchQuery();
    const { height, ref } = useSize();
    const { children, adults, infants } = searchQuery || {};
    const { usePointsPlusPay } = useCustomerSelection();
    const shouldUseNewEvents = useGA4Events();

    const breakpoints = useBreakpoints();
    const isMobile = isScreen(breakpoints)('xxs', 'xs');

    const confirmButtonText = isMobile
      ? 'Confirm'
      : getConfirmButtonLabel(direction);

    const pricingProps = {
      offerId,
      charges,
      travellers: {
        children,
        adults,
        infants,
      },
    };

    const onCtaClick = () => {
      if (shouldUseNewEvents) {
        dataLayer.ctaClickEvent({
          itemText: confirmButtonText,
        });
      }
      onDone();
    };

    return (
      <Box height={height}>
        <Box
          ref={ref}
          bg="white"
          borderTop="1px solid"
          borderColor="greys.alto"
          position="fixed"
          bottom="0"
          left="0"
          right="0"
        >
          <Container>
            <Flex
              py="6"
              flexDirection="row"
              alignItems="flex-start"
              justifyContent="space-between"
            >
              <Box>
                <PackageHeroSummary
                  searchQuery={searchQuery}
                  showNights={Boolean(searchQuery)}
                  exact={true}
                />
                {isMobile ? (
                  <PackagePagePriceWithCompactPointsPlusPay {...pricingProps} />
                ) : (
                  <PackagePagePriceWithPointsPlusPay {...pricingProps} />
                )}
                {usePointsPlusPay && (
                  <Flex>
                    <Text fontSize="2xs" color="greys.steel">
                      You can change the points and cash value at payment
                    </Text>
                  </Flex>
                )}
              </Box>
              <StyledButton
                data-testid="CTA"
                variant="primary"
                onClick={onCtaClick}
              >
                {confirmButtonText}
              </StyledButton>
            </Flex>
          </Container>
        </Box>
      </Box>
    );
  },
);

export default FlightSummary;
